import styled from "styled-components";
import EvidenceAPI from "../../api/evidence/index.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@mui/material";
import { useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import CreateEvidenceChildModal from "@/Features/Evidence/Modals/CreateEvidenceChildModal";
import { Evidence } from "@/Features";
import {
  caseIdFromSearchParams,
  evidenceDetailPath,
} from "@/Features/Evidence/evidenceLinks";

interface ChildItemsContentProps {
  className?: string;
  evidenceInfo: Evidence;
}

interface EvidenceWithChildren extends Evidence {
  child_list?: Evidence[];
}

const ChildItemsContent = styled(
  ({ className, evidenceInfo }: ChildItemsContentProps) => {
    const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const queryClient = useQueryClient();
    // Read the case origin from the URL (the source of truth) rather than the async
    // CaseProvider data, so child links always carry ?case= even while the case loads.
    const [searchParams] = useSearchParams();
    const caseId = caseIdFromSearchParams(searchParams);
    const { isLoading, data } = useQuery<
      EvidenceWithChildren[],
      Error,
      EvidenceWithChildren
    >({
      queryKey: [
        "evidence",
        "list",
        { uuid: evidenceInfo.uuid, include_children: true },
      ],
      queryFn: () =>
        EvidenceAPI.getEvidence({
          uuid: evidenceInfo.uuid,
          include_children: true,
        }),
      select: (data) => data[0],
    });

    const onCreate = () => {
      queryClient.refetchQueries({
        queryKey: [
          "evidence",
          "list",
          { uuid: evidenceInfo.uuid, include_children: true },
        ],
      });
    };

    const childItems: Evidence[] = data?.child_list || [];

    return (
      <div className={className}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setShowCreateModal(true)}
          disabled={!hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_CREATE)}
          style={{
            minWidth: "fit-content",
            fontSize: 11,
            padding: "3px 6px",
          }}
        >
          + New Child Item
        </Button>
        <div className="child-items-grid">
          {childItems.length === 0 && (
            <div style={{ marginTop: 10, marginBottom: 20 }}>
              No child evidence items.
            </div>
          )}
          {childItems.map((item: Evidence) => {
            return <ChildItem key={item.uuid} item={item} caseId={caseId} />;
          })}
        </div>
        <CreateEvidenceChildModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={onCreate}
          onCancel={() => setShowCreateModal(false)}
          defaultInfo={evidenceInfo}
        />
      </div>
    );
  }
)`
  .child-items-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: 20px;
    margin-top: 20px;
  }
`;

interface ChildItemProps {
  className?: string;
  item: Evidence;
  caseId?: number;
}

const ChildItem = styled(({ className, item, caseId }: ChildItemProps) => {
  return (
    <div className={className}>
      <Link to={evidenceDetailPath(item.uuid, { caseId })}>
        <div className="evidence-number">{item.evidence_number}</div>
      </Link>

      <div className="item-type-label">{item.item_type}</div>
      <div className="provider-label">
        <div>{item.manufacturer || "No Provider"}</div>
        <div>{item.serial_number || "No Unique ID"}</div>
      </div>
    </div>
  );
})`
  min-height: 100px;
  width: 300px;
  max-width: 300px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 5px;
  padding: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  a {
    width: fit-content;
  }
  .evidence-number {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 5px;
    color: ${({ theme }) => theme.palette.primary.main};
    width: fit-content;
    :hover {
      text-decoration: underline;
      cursor: pointer;
    }
  }
  .item-type-label {
    font-size: 12px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
  .provider-label {
    font-size: 12px;
    color: ${({ theme }) => theme.palette.text.secondary};
    bottom: 10px;
    display: flex;
    justify-content: space-between;
    margin-top: auto;
  }
`;

export default ChildItemsContent;
